import { supabase, friendlyError } from "../supabase.js";
import { carryCodes } from "./carryCodes.js";

/* Supabase takes a large insert happily but a 546-row body is a long time to
   wait with nothing on screen, so it goes in batches and reports progress. */
const BATCH = 200;

/* Writing a schedule is the one import that cannot be a flat insert, because
   the rows point at each other. The plan is a tree and the hierarchy is the
   part that makes it worth having.
 *
 * Levels go in shallowest first, and each level's rows come back carrying the
 * ids the database gave them. By the time a child is written, its parent's id
 * is already known, so parent_id is set on the way in rather than patched
 * afterwards. A row's parent is the row whose WBS is its own with the last
 * segment removed, which is true by construction in both MS Project and
 * Primavera.
 *
 * Nothing here is destructive. Replacing an existing schedule sets deleted_at
 * on the old rows: anyone working the contract may retire a row, and only the
 * owner may destroy one, and the database is what enforces that.
 */
export async function writeSchedule({
  contractId, activities, links = [], mode = "replace", userId = null,
  onProgress = () => {},
}) {
  if (!contractId) return { ok: false, error: "No contract is active." };
  if (!activities?.length) return { ok: false, error: "There is nothing to import." };

  let retired = 0;
  /* Which incoming activity keeps which existing code. Empty unless this is a
     replace, because an append is new work rather than the same work again. */
  let keepCode = new Map();

  if (mode === "replace") {
    onProgress({ phase: "clearing" });

    /* Read before retiring, and read the codes, because they are the point.
       Without this the database mints fresh codes for every row, the baseline
       is stored against the old ones, and the Schedule page comes back
       measuring nothing while looking entirely normal. */
    const { data: before, error: readError } = await supabase
      .from("schedule_stages")
      .select("activity_code, activity_id, title")
      .eq("contract_id", contractId)
      .is("deleted_at", null);
    if (readError) return { ok: false, error: friendlyError(readError) };

    keepCode = carryCodes(before ?? [], activities);

    const { data, error } = await supabase
      .from("schedule_stages")
      .update({ deleted_at: new Date().toISOString(), updated_by: userId })
      .eq("contract_id", contractId)
      .is("deleted_at", null)
      .select("id");
    if (error) return { ok: false, error: friendlyError(error) };
    retired = data?.length ?? 0;
  }

  /* carryCodes works in the order activities were given, and the rows are
     written level by level, so the position in the original array is what
     ties the two together. */
  const positionOf = new Map(activities.map((a, at) => [a, at]));

  const levels = [...new Set(activities.map((a) => Number(a.outline_level) || 0))]
    .sort((a, b) => a - b);

  const idByWbs = new Map();
  /* The file's own UID is the only thing a predecessor link points at. Kept
     only for the length of this import: the app issues its own permanent code
     through a database trigger, because MS Project reuses UIDs across files
     and they do not survive a rebaseline. */
  const idByUid = new Map();
  let written = 0;

  for (const level of levels) {
    const rows = activities.filter((a) => (Number(a.outline_level) || 0) === level);

    for (let i = 0; i < rows.length; i += BATCH) {
      const chunk = rows.slice(i, i + BATCH).map((a) => ({
        contract_id: contractId,
        title: a.title,
        activity_id: a.activity_id,
        outline_level: a.outline_level,
        start_date: a.start_date,
        finish_date: a.finish_date,
        percent_complete: a.percent_complete,
        workstream: a.workstream ?? null,
        start_is_actual: !!a.start_is_actual,
        finish_is_actual: !!a.finish_is_actual,
        notes: a.notes ?? null,
        is_milestone: !!a.is_milestone,
        /* Set only where this activity was here before. Left null, the
           database trigger mints a new code, which is what a genuinely new
           activity should get. */
        activity_code: keepCode.get(positionOf.get(a)) ?? null,
        /* Every row records who put it there, imports included. */
        added_by: userId,
        updated_by: userId,
        parent_id: parentIdFor(a.activity_id, idByWbs),
      }));

      const uids = rows.slice(i, i + BATCH).map((a) => a.uid ?? null);

      const { data, error } = await supabase
        .from("schedule_stages")
        .insert(chunk)
        .select("id, activity_id");

      if (error) {
        return {
          ok: false, written, retired,
          error: friendlyError(error),
          partial: written > 0,
        };
      }

      for (const row of data ?? []) {
        if (row.activity_id) idByWbs.set(row.activity_id, row.id);
      }
      /* Insert returns rows in the order they were sent, which is how a UID
         is matched to the id the database just minted for it. */
      (data ?? []).forEach((row, at) => {
        if (uids[at]) idByUid.set(uids[at], row.id);
      });
      written += chunk.length;
      onProgress({ phase: "writing", written, total: activities.length });
    }
  }

  /* The logic, once every activity has an id to point at. A link whose other
     end did not make it in is dropped rather than written as a dangling row:
     the database would refuse it anyway, and refusing the whole import over
     one bad link would be a poor trade. */
  let linksWritten = 0;
  const usable = links
    .map((l) => ({
      contract_id: contractId,
      predecessor_id: idByUid.get(l.from),
      successor_id: idByUid.get(l.to),
      link_type: l.link_type ?? "FS",
      lag_days: Number.isFinite(l.lag_days) ? l.lag_days : 0,
      added_by: userId,
      updated_by: userId,
    }))
    .filter((l) => l.predecessor_id && l.successor_id
                && l.predecessor_id !== l.successor_id);

  for (let i = 0; i < usable.length; i += BATCH) {
    const chunk = usable.slice(i, i + BATCH);
    const { error } = await supabase.from("schedule_links").insert(chunk);
    if (error) {
      return {
        ok: false, written, retired, linksWritten,
        error: `The activities imported but the logic did not: ${friendlyError(error)}`,
        partial: true,
      };
    }
    linksWritten += chunk.length;
    onProgress({ phase: "linking", written: linksWritten, total: usable.length });
  }

  return {
    ok: true, written, retired,
    kept: keepCode.size,
    linked: countLinked(activities, idByWbs),
    linksWritten,
    linksDropped: links.length - usable.length,
  };
}

function parentIdFor(activityId, idByWbs) {
  if (!activityId || !activityId.includes(".")) return null;
  const parent = activityId.slice(0, activityId.lastIndexOf("."));
  return idByWbs.get(parent) ?? null;
}

const countLinked = (activities, idByWbs) =>
  activities.filter((a) => parentIdFor(a.activity_id, idByWbs)).length;
